/**
 * DatePickerWithMarkers
 * 
 * A date picker that highlights key dates with clickable legend categories:
 * - 🔵 Sprint Close / Release-Sprint (based on release strategy) - cycles through planned releases
 * - 🔷 Release (projected) - cycles through projected releases
 * - 🟠 Goal deadlines - one row per goal, jumps to that goal's target_date
 * 
 * Legend shows CATEGORY rows (not individual dates). Click to jump, click again to cycle.
 * Calendar opens to selected date's month, or today if no value.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { format, parseISO, isSameDay } from 'date-fns';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { Calendar as CalendarIcon, ChevronRight, MapPin } from 'lucide-react';
import { useProductDateCache } from '../../contexts/ProductDateCacheContext';

/**
 * Props:
 * - value: string (ISO date) or Date
 * - onChange: (date: string) => void
 * - parentDeadlines: Array<{ date: string, label: string, goal_id?: string }> - Goal deadlines
 * - placeholder: string
 * - disabled: boolean
 * - className: string
 */
export function DatePickerWithMarkers({
  value,
  onChange,
  parentDeadlines = [],
  placeholder = "Select date",
  disabled = false,
  className,
}) {
  const { getAllReleaseEndDates, cache } = useProductDateCache();
  const [displayMonth, setDisplayMonth] = useState(null);
  
  // Track cycling state: which category and current index
  const [cycleState, setCycleState] = useState({ category: null, index: 0 });

  // Check if release strategy is continuous (CD)
  const isContinuousDeployment = cache?.releases?.settings?.release_strategy === 'continuous';

  // Parse the current value
  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    try {
      return typeof value === 'string' ? parseISO(value) : value;
    } catch {
      return undefined;
    }
  }, [value]);

  // Determine which month to display: priority is displayMonth > selectedDate > today
  const calendarMonth = useMemo(() => {
    if (displayMonth) return displayMonth;
    if (selectedDate) return selectedDate;
    return new Date();
  }, [displayMonth, selectedDate]);

  // Build release date arrays (sorted by date)
  const { plannedReleaseDates, projectedReleaseDates } = useMemo(() => {
    const planned = [];
    const projected = [];

    getAllReleaseEndDates.forEach(item => {
      try {
        const date = parseISO(item.date);
        if (item.type === 'release_planned') {
          planned.push({ date: item.date, dateObj: date, label: item.label });
        } else if (item.type === 'release_projected') {
          projected.push({ date: item.date, dateObj: date, label: item.label });
        }
      } catch {
        // Skip invalid dates
      }
    });

    // Sort by date
    planned.sort((a, b) => a.dateObj - b.dateObj);
    projected.sort((a, b) => a.dateObj - b.dateObj);

    return { plannedReleaseDates: planned, projectedReleaseDates: projected };
  }, [getAllReleaseEndDates]);

  // Build modifiers for highlighting dates on calendar
  const { modifiers, modifiersStyles } = useMemo(() => {
    const releasePlannedDates = plannedReleaseDates.map(r => r.dateObj);
    const releaseProjectedDates = projectedReleaseDates.map(r => r.dateObj);
    const parentDates = [];

    parentDeadlines.forEach(item => {
      try {
        const date = typeof item.date === 'string' ? parseISO(item.date) : item.date;
        parentDates.push(date);
      } catch {
        // Skip invalid dates
      }
    });

    return {
      modifiers: {
        releasePlanned: releasePlannedDates,
        releaseProjected: releaseProjectedDates,
        parentDeadline: parentDates,
      },
      modifiersStyles: {
        releasePlanned: {
          backgroundColor: '#3b82f6',
          color: 'white',
          borderRadius: '50%',
        },
        releaseProjected: {
          backgroundColor: '#93c5fd',
          color: '#1e3a8a',
          borderRadius: '50%',
        },
        parentDeadline: {
          backgroundColor: '#f59e0b',
          color: 'white',
          borderRadius: '50%',
        },
      },
    };
  }, [plannedReleaseDates, projectedReleaseDates, parentDeadlines]);

  // Handle clicking on a category to jump/cycle
  const handleCategoryClick = useCallback((category, dates) => {
    if (!dates || dates.length === 0) return;
    
    let targetIndex = 0;
    
    if (cycleState.category === category && dates.length > 1) {
      // Same category clicked again - cycle to next
      targetIndex = (cycleState.index + 1) % dates.length;
    } else {
      // Different category or first click - start at first date
      targetIndex = 0;
    }
    
    setCycleState({ category, index: targetIndex });
    
    const targetDate = dates[targetIndex];
    if (targetDate) {
      try {
        const date = typeof targetDate.date === 'string' ? parseISO(targetDate.date) : targetDate.date;
        setDisplayMonth(date);
      } catch {
        // Skip invalid dates
      }
    }
  }, [cycleState]);

  // Handle clicking on a single goal (no cycling, just jump)
  const handleGoalClick = useCallback((goalDeadline) => {
    if (!goalDeadline?.date) return;
    
    setCycleState({ category: `goal_${goalDeadline.goal_id}`, index: 0 });
    
    try {
      const date = typeof goalDeadline.date === 'string' ? parseISO(goalDeadline.date) : goalDeadline.date;
      setDisplayMonth(date);
    } catch {
      // Skip invalid dates
    }
  }, []);

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'));
    } else {
      onChange('');
    }
  };

  const handleMonthChange = (month) => {
    setDisplayMonth(month);
  };

  // Determine labels based on release strategy
  const sprintLabel = isContinuousDeployment ? 'Release-Sprint' : 'Sprint Close';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, 'PPP') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          month={calendarMonth}
          onMonthChange={handleMonthChange}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          initialFocus
        />
        
        {/* Legend - Category rows only */}
        {(plannedReleaseDates.length > 0 || projectedReleaseDates.length > 0 || parentDeadlines.length > 0) && (
          <div className="border-t px-3 py-2 space-y-1">
            <div className="text-[10px] text-muted-foreground/60 uppercase tracking-wide mb-1">
              Click to jump • Click again to cycle
            </div>
            
            {/* Sprint Close / Release-Sprint row */}
            {plannedReleaseDates.length > 0 && (
              <div 
                className={cn(
                  "flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5 transition-colors",
                  cycleState.category === 'planned' 
                    ? "bg-muted text-foreground font-medium" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
                onClick={() => handleCategoryClick('planned', plannedReleaseDates)}
                title={`Click to cycle through ${plannedReleaseDates.length} dates`}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-blue-500" />
                <span className="flex-1">{sprintLabel}</span>
                {plannedReleaseDates.length > 1 && (
                  <span className="text-[10px] opacity-60">
                    {cycleState.category === 'planned' ? cycleState.index + 1 : 1}/{plannedReleaseDates.length}
                  </span>
                )}
                <ChevronRight className="w-3 h-3 opacity-40 flex-shrink-0" />
              </div>
            )}
            
            {/* Release (projected) row */}
            {projectedReleaseDates.length > 0 && (
              <div 
                className={cn(
                  "flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5 transition-colors",
                  cycleState.category === 'projected' 
                    ? "bg-muted text-foreground font-medium" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
                onClick={() => handleCategoryClick('projected', projectedReleaseDates)}
                title={`Click to cycle through ${projectedReleaseDates.length} dates`}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-blue-300" />
                <span className="flex-1">Release (projected)</span>
                {projectedReleaseDates.length > 1 && (
                  <span className="text-[10px] opacity-60">
                    {cycleState.category === 'projected' ? cycleState.index + 1 : 1}/{projectedReleaseDates.length}
                  </span>
                )}
                <ChevronRight className="w-3 h-3 opacity-40 flex-shrink-0" />
              </div>
            )}
            
            {/* Goal deadline rows - one per goal */}
            {parentDeadlines.map((goal, idx) => (
              <div 
                key={goal.goal_id || idx}
                className={cn(
                  "flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5 transition-colors",
                  cycleState.category === `goal_${goal.goal_id}` 
                    ? "bg-muted text-foreground font-medium" 
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
                onClick={() => handleGoalClick(goal)}
                title="Click to jump to goal deadline"
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-amber-500" />
                <span className="truncate flex-1">{goal.label}</span>
                <MapPin className="w-3 h-3 opacity-40 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Simple date input wrapper that uses DatePickerWithMarkers
 */
export function DateInputWithMarkers({
  value,
  onChange,
  parentDeadlines = [],
  placeholder = "Select date",
  disabled = false,
  className,
}) {
  return (
    <DatePickerWithMarkers
      value={value}
      onChange={onChange}
      parentDeadlines={parentDeadlines}
      placeholder={placeholder}
      disabled={disabled}
      className={cn("h-9", className)}
    />
  );
}

export default DatePickerWithMarkers;
